import { Dispatch, SetStateAction } from "react";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import IccidPukHint from "@/assets/onboarding/sim-verification/IccidAndPuk.svg?react";
import { CameraScanner } from "@/components/Checkout/CameraScanner/CameraScanner";
import { HintOverlay } from "@/components/Checkout/Steps/SimVerification/HintOverlay/HintOverlay";
import {
  formatIccid,
  SimVerificationFormProps,
} from "@/components/Checkout/Steps/SimVerification/simVerificationForm";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";

type Props = {
  register: UseFormRegister<SimVerificationFormProps>;
  errors: FieldErrors<SimVerificationFormProps>;
  setValue: UseFormSetValue<SimVerificationFormProps>;
  pukMandatory: boolean;
  simUnavailable: boolean;
  showOverlay: boolean;
  setShowOverlay: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
};

/**
 * Shared ICCID / PUK fields + camera scan used by SimSelection (combined step 1)
 * and the standalone SimVerification route (purchase intents / back-nav).
 */
export const PhysicalSimFields = ({
  register,
  errors,
  setValue,
  pukMandatory,
  simUnavailable,
  showOverlay,
  setShowOverlay,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex justify-end tablet:hidden">
            <button
              className="text-body-xs !font-semibold leading-none tracking-[0.24px] text-foreground-primary"
              type="button"
              autoFocus={false}
              tabIndex={-1}
              disabled={disabled}
              onClick={() => setShowOverlay(!showOverlay)}
            >
              {t("Onboarding.sections.sim-verification.iccid.helpHint")}
            </button>
          </div>
          <FormField<SimVerificationFormProps>
            id="iccd-input"
            label={t("Onboarding.sections.sim-verification.iccid.label")}
            name="iccid"
            icon={
              <CameraScanner
                iconOnly
                disabled={disabled}
                onScan={(value) => {
                  setValue("iccid", formatIccid(value));
                }}
              />
            }
            errors={errors}
            register={register}
            type="text"
            placeholder={t(
              "Onboarding.sections.sim-verification.iccid.placeholder",
            )}
            inputMode="numeric"
            options={{
              required: t("label.validation.required"),
              disabled,
              validate: () => {
                if (simUnavailable) {
                  return String(
                    t(
                      "Onboarding.sections.sim-verification.iccid.validation.invalid",
                    ),
                  );
                }

                return true;
              },
              pattern: {
                value: /^\d{6}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{2}$/,
                message: t(
                  "Onboarding.sections.sim-verification.iccid.validation.badFormat",
                ),
              },
              onChange: (e: KeyboardEvent) => {
                const input = e.target as HTMLInputElement;
                setValue("iccid", formatIccid(input.value));
              },
              iccidValidError: t(
                "Onboarding.sections.sim-verification.iccid.validation.invalid",
              ),
            }}
          />
        </div>
        <FormField<SimVerificationFormProps>
          id="puk-input"
          label={t("Onboarding.sections.sim-verification.puk.label")}
          name="puk"
          errors={errors}
          register={register}
          type="text"
          placeholder={t(
            "Onboarding.sections.sim-verification.puk.placeholder",
          )}
          inputMode="numeric"
          options={{
            required: pukMandatory,
            disabled: disabled || !pukMandatory,
            pattern: {
              value: /^\d{8}$/,
              message: t(
                "Onboarding.sections.sim-verification.puk.validation.badFormat",
              ),
            },
            onChange: (e: KeyboardEvent) => {
              const input = e.target as HTMLInputElement;
              let value = input.value;
              if (value.length > 8) {
                value = value.substring(0, 8);
              }
              setValue("puk", value);
            },
          }}
        />
      </div>

      <HintOverlay
        setVisible={setShowOverlay}
        visible={showOverlay}
        hint={<IccidPukHint />}
      />
    </>
  );
};
